import { Task, TaskStatus } from "../types";
import {
  getFreezedTasks,
  getCheckStatus,
  getCheckSubscriptionStatus,
  getCheckSubscriptionStatusTappAds,
  getPartnerTasks,
  getPartnerSubscriptionTasks,
  postPartnerTasksGetReward,
  getCheckStatusFlyer,
  getCheckSubStatusFlyer,
  claimFlyerTask,
} from "../../../Services/Requests";
import {
  SponsorsTasksAdapter,
  SponsorType,
} from "../adapters/SponsorsTasksAdapter";
import { BarzhaResponse } from "../adapters/sponsors/types/barzha";
import { FlyerAdapter } from "../adapters/sponsors/FlyerAdapter";
import { DreamcoinResponse } from "../adapters/sponsors/types/dreamcoin";
import { useNotificationStore } from "../../../Store/NotificationStore";
import { BarzhaClickService } from "./BarzhaClickService";
import { BarzhaClickAdapter } from "../adapters/sponsors/BarzhaClickAdapter";
import { useTasksStore } from "../../../Store/TasksStore";

type BarzhaCallback = {
  (response: BarzhaResponse): void;
  (): void;
};

interface SponsorProvider {
  type: SponsorType;
  getTasks: () => Promise<Task[]>;
}

export class SponsorsService {
  private static sponsorTasks: Task[] = [];

  static async getSponsorTasks(): Promise<Task[]> {
    try {
      console.log("🚀 SponsorsService.getSponsorTasks - start");

      // Определяем провайдеры заданий
      const sponsorProviders: SponsorProvider[] = [
        {
          type: SponsorType.DREAMCOIN_CLICK,
          getTasks: async () => {
            try {
              const dreamcoinClickResponse = await getPartnerTasks();
              return SponsorsTasksAdapter.adaptTasks(
                SponsorType.DREAMCOIN_CLICK,
                dreamcoinClickResponse as DreamcoinResponse
              );
            } catch (error) {
              console.error(
                "❌ Ошибка получения заданий от Dreamcoin Click:",
                error
              );
              return [];
            }
          },
        },
        {
          type: SponsorType.BARZHA_CLICK,
          getTasks: async () => {
            try {
              console.log("🎯 SponsorsService - получение заданий BarzhaClick");

              // Создаем Promise для таймаута
              const timeout = new Promise<Task[]>((_, reject) => {
                setTimeout(() => {
                  reject(
                    new Error("Таймаут загрузки заданий BarzhaClick (5 сек)")
                  );
                }, 3000);
              });

              // Race между получением заданий и таймаутом
              const tasks = await Promise.race([
                (async () => {
                  await BarzhaClickService.initialize();
                  const response = await BarzhaClickService.getAds();
                  console.log(
                    "🎯 SponsorsService - полученный ответ:",
                    response
                  );

                  if (response) {
                    const tasks = await BarzhaClickAdapter.adaptTasks(response);
                    console.log(
                      "🎯 SponsorsService - адаптированные задания:",
                      tasks
                    );
                    return tasks;
                  }
                  return [];
                })(),
                timeout,
              ]).catch((error) => {
                console.warn("⚠️ BarzhaClick:", error.message);
                return [];
              });

              return tasks;
            } catch (error) {
              console.error(
                "❌ Ошибка получения заданий от BarzhaClick:",
                error
              );
              return [];
            }
          },
        },
        {
          type: SponsorType.DREAMCOIN_SUBSCRIBE,
          getTasks: async () => {
            try {
              const dreamcoinSubscribeResponse =
                await getPartnerSubscriptionTasks();

              return SponsorsTasksAdapter.adaptTasks(
                SponsorType.DREAMCOIN_SUBSCRIBE,
                dreamcoinSubscribeResponse as DreamcoinResponse
              );
            } catch (error) {
              console.error(
                "❌ Ошибка получения заданий от Dreamcoin Subscribe:",
                error
              );
              return [];
            }
          },
        },
        {
          type: SponsorType.BARZHA,
          getTasks: async () => {
            try {
              // throw new Error('Симуляция ошибки Barzha');
              const [barzhaResponse, barzhaStatusResponse] = await Promise.all([
                new Promise<BarzhaResponse>((resolve) => {
                  const callback: BarzhaCallback = (
                    response?: BarzhaResponse
                  ) => {
                    if (response) {
                      console.log("✨ Получен ответ от Barzha:", response);
                      resolve(response);
                    }
                  };
                  getFreezedTasks(callback);
                }),
                getCheckStatus(),
              ]);

              return SponsorsTasksAdapter.adaptTasks(
                SponsorType.BARZHA,
                barzhaResponse,
                barzhaStatusResponse
              );
            } catch (error) {
              console.error("❌ Ошибка получения заданий от Barzha:", error);
              return [];
            }
          },
        },
        // {
        //   type: SponsorType.TAPPADS,
        //   getTasks: async () => {
        //     try {
        //       // throw new Error('Симуляция ошибки TappAds');
        //       const [tappAdsFeed, tappAdsStatusTasks] = await Promise.all([
        //         TappAdsAdapter.getFeed(),
        //         TappAdsAdapter.getStatusTasks(),
        //       ]);

        //       return SponsorsTasksAdapter.adaptTasks(
        //         SponsorType.TAPPADS,
        //         tappAdsFeed,
        //         tappAdsStatusTasks
        //       );
        //     } catch (error) {
        //       console.error("❌ Ошибка получения заданий от TappAds:", error);
        //       return [];
        //     }
        //   },
        // },
        {
          type: SponsorType.FLYER,
          getTasks: async () => {
            try {
              const [flyerTasks, flyerStatusTasks] = await Promise.all([
                FlyerAdapter.getFeed(),
                FlyerAdapter.getStatusTasks(),
              ]);

              console.log("📦 Получены задания от Flyer:", {
                flyerTasks,
                flyerStatusTasks,
              });

              return SponsorsTasksAdapter.adaptTasks(
                SponsorType.FLYER,
                flyerTasks,
                flyerStatusTasks
              );
            } catch (error) {
              console.error("❌ Ошибка получения заданий от Flyer:", error);
              return [];
            }
          },
        },
      ];

      // Получаем задания от всех провайдеров параллельно
      const tasksPromises = sponsorProviders.map((provider) =>
        provider.getTasks().catch((error) => {
          console.error(
            `❌ Ошибка получения заданий от ${provider.type}:`,
            error
          );
          return [];
        })
      );

      const tasksArrays = await Promise.all(tasksPromises);

      const allTasks = tasksArrays.flat();

      // Сортировка заданий по статусу
      const sortedTasks = this.sortTasksByStatus(allTasks);

      // Сохраняем задания в памяти
      this.sponsorTasks = sortedTasks;

      console.log("📋 Адаптированные и отсортированные задания:", sortedTasks);

      return sortedTasks;
    } catch (error) {
      console.error("❌ SponsorsService.getSponsorTasks - error:", error);
      throw error;
    }
  }

  private static sortTasksByStatus(tasks: Task[]): Task[] {
    const statusPriority: Record<TaskStatus, number> = {
      NOT_STARTED: 1,
      IN_PROGRESS: 2,
      WAITING: 3,
      DONE: 4,
      CLAIMED: 5,
    };

    return [...tasks].sort((a, b) => {
      return statusPriority[a.status] - statusPriority[b.status];
    });
  }

  static async claimSponsorTask(
    taskId: number | string
  ): Promise<boolean | string> {
    try {
      // Ищем задание в кэше
      const task = this.sponsorTasks.find((task) => task.id === taskId);
      console.log("🚀 SponsorsService.claimSponsorTask - task", task);

      if (!task) {
        console.warn("⚠️ Task not found with ID:", taskId);
        return false;
      }

      let waiting = false;
      let done = false;
      let success = false;
      let rewardAmount = 0;
      let rewardType = "tickets";

      switch (task.provider) {
        case "BARZHA_CLICK": {
          // Используем taskId напрямую, так как это и есть exchange_ad_id
          success = await BarzhaClickService.claimReward(taskId);
          if (success) {
            rewardAmount = 1;
            rewardType = "tickets";
          }
          break;
        }
        case "BARZHA": {
          const barzhaResponse = await getCheckSubscriptionStatus(taskId);
          if (barzhaResponse) {
            success = true;
            // Предполагаем, что в ответе есть информация о награде
            if (barzhaResponse.amount) {
              rewardAmount = barzhaResponse.amount;
              rewardType = barzhaResponse.type || "tickets";
            }
          }
          break;
        }

        // case "TAPPADS": {
        //   const tappAdsResponse = await getCheckSubscriptionStatusTappAds(
        //     taskId
        //   );
        //   if (tappAdsResponse) {
        //     success = true;
        //     // Предполагаем, что в ответе есть информация о награде
        //     if (tappAdsResponse.amount) {
        //       rewardAmount = tappAdsResponse.amount;
        //       rewardType = tappAdsResponse.type || "tickets";
        //     }
        //   }
        //   break;
        // }

        case "FLYER": {
          if (task.status === "DONE") {
            const res = await claimFlyerTask(String(taskId));

            if (res) {
              success = true;

              if (res.amount) {
                rewardAmount = res.amount;
                rewardType = res.type || "tickets";
              }
            }
            break;
          } else {
            const res = await getCheckSubStatusFlyer(
              String(taskId),
              task.title,
              task.description || "-",
              task.icon || "-"
            );

            if (res) {
              if (res.status === "WAITING") {
                waiting = true;
              } else if (res.status === "DONE") {
                done = true;
              } else {
                success = true;
              }
            }
            break;
          }
        }

        case "DREAMCOIN_CLICK":
        case "DREAMCOIN_SUBSCRIBE": {
          const dreamcoinResponse = await postPartnerTasksGetReward(taskId);
          console.log("Dreamcoin response:", dreamcoinResponse);

          if (dreamcoinResponse && dreamcoinResponse.status === "CLAIMED") {
            success = true;

            // Проверяем наличие массива rewards_processed
            if (
              dreamcoinResponse.rewards_processed &&
              dreamcoinResponse.rewards_processed.length > 0
            ) {
              const reward = dreamcoinResponse.rewards_processed[0];
              rewardAmount = reward.amount || 0;
              rewardType = reward.type || "tickets";
            }
          }
          break;
        }

        default:
          console.warn("⚠️ Unknown sponsor provider:", task.provider);
          return false;
      }

      const setNotification = useNotificationStore.getState().setNotification;

      if (waiting) {
        this.updateTaskStatus(taskId, "WAITING");
        setNotification({
          type: "Warning",
          message: "Задание на проверке.\nНе отписывайтесь 24 часа.",
        });
        return "WAITING";
      } else if (done) {
        this.updateTaskStatus(taskId, "DONE");
        return "DONE";
      } else if (success) {
        // Обновляем статус задания в кэше
        this.updateTaskStatus(taskId, "CLAIMED");

        // Убедимся, что тип награды соответствует ожидаемому формату
        // Если тип 'ticket', изменим его на 'tickets' для совместимости
        const normalizedRewardType =
          rewardType === "ticket" ? "tickets" : rewardType;

        setNotification({
          type: "Success",
          message: "Награда за задание получена!",
          rewards:
            rewardAmount > 0
              ? [{ amount: rewardAmount, type: normalizedRewardType }]
              : undefined,
        });
        return true;
      } else {
        setNotification({
          type: "Error",
          message: "Задание на проверке. Повтори через 30 секунд.",
        });
        return false;
      }
    } catch (error) {
      const task = this.sponsorTasks.find((task) => task.id === taskId);

      if (!task) {
        console.warn("⚠️ Task not found with ID:", taskId);
        return false;
      }

      console.error("❌ SponsorsService.claimSponsorTask - error", error);
      const setNotification = useNotificationStore.getState().setNotification;

      if (task.provider === "FLYER") {
        setNotification({
          type: "Error",
          message: "Задание не выполнено.",
        });
      } else {
        setNotification({
          type: "Error",
          message: "Задание на проверке. Повтори через 30 секунд.",
        });
      }
      return false;
    }
  }

  // Добавляем метод для обновления статуса задания
  private static updateTaskStatus(
    taskId: number | string,
    status: TaskStatus
  ): void {
    const taskIndex = this.sponsorTasks.findIndex((task) => task.id === taskId);
    if (taskIndex !== -1) {
      this.sponsorTasks[taskIndex] = {
        ...this.sponsorTasks[taskIndex],
        status,
      };
      console.log(`✅ Статус задания ${taskId} обновлен на ${status}`);

      // Синхронизируем с основным стором приложения
      try {
        const { tasks } = useTasksStore.getState();
        const updatedTasks = tasks.map((task) =>
          task.id === taskId ? { ...task, status } : task
        );
        useTasksStore.getState().setTasks(updatedTasks);
        console.log(
          `🔄 Статус задания ${taskId} синхронизирован с основным стором`
        );
      } catch (error) {
        console.warn(
          `⚠️ Не удалось синхронизировать статус задания ${taskId} с основным стором:`,
          error
        );
      }
    }
  }
}
